import Masonry from "@mui/lab/Masonry";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ChatCircleText } from "@phosphor-icons/react/ChatCircleText";
import { Clock } from "@phosphor-icons/react/Clock";
import { Cloud } from "@phosphor-icons/react/Cloud";
import { Database } from "@phosphor-icons/react/Database";
import { DeviceMobile } from "@phosphor-icons/react/DeviceMobile";
import { Drone } from "@phosphor-icons/react/Drone";
import { FolderSimple } from "@phosphor-icons/react/FolderSimple";
import { LinkSimple } from "@phosphor-icons/react/LinkSimple";
import { Printer } from "@phosphor-icons/react/Printer";
import { FilePlus } from "@phosphor-icons/react/FilePlus";
import { Globe } from "@phosphor-icons/react/Globe";
import { Lightning } from "@phosphor-icons/react/Lightning";
import { Link } from "@phosphor-icons/react/Link";
import { ListMagnifyingGlass } from "@phosphor-icons/react/ListMagnifyingGlass";
import { Lock } from "@phosphor-icons/react/Lock";
import { MagnifyingGlass } from "@phosphor-icons/react/MagnifyingGlass";
import { MapPin } from "@phosphor-icons/react/MapPin";
import { MapPinArea } from "@phosphor-icons/react/MapPinArea";
import { MonitorArrowUp } from "@phosphor-icons/react/MonitorArrowUp";
import { PaintBrush } from "@phosphor-icons/react/PaintBrush";
import { Pencil } from "@phosphor-icons/react/Pencil";
import { Rocket } from "@phosphor-icons/react/Rocket";
import { Shapes } from "@phosphor-icons/react/Shapes";
import { ShieldCheck } from "@phosphor-icons/react/ShieldCheck";
import { Stack as StackIcon } from "@phosphor-icons/react/Stack";
import { Tag } from "@phosphor-icons/react/Tag";
import { Timer } from "@phosphor-icons/react/Timer";
import { Translate } from "@phosphor-icons/react/Translate";
import { UploadSimple } from "@phosphor-icons/react/UploadSimple";
import { Users } from "@phosphor-icons/react/Users";
import { motion, useInView, useReducedMotion } from "framer-motion";
import * as React from "react";
import { headingFontFamily } from "../heading-font";

const entrySpring = {
	stiffness: 260,
	damping: 18,
	mass: 0.85,
};

const featureCardRadius = "20px";
const featureGridSafeArea = {
	md: 2.5,
	sm: 2,
	xs: 1.5,
} as const;
const featureGridFadeTint =
	"linear-gradient(to bottom, rgba(248, 250, 252, 0) 0%, rgba(248, 250, 252, 0.72) 52%, rgba(248, 250, 252, 0.9) 78%, rgba(248, 250, 252, 0.96) 100%)";

interface FeatureItem {
	title: string;
	description: string;
	label: string;
	icon: React.ReactNode;
	comingSoon?: boolean;
}

function useActiveFeatureColumnCount() {
	const [columnCount, setColumnCount] = React.useState(5);

	React.useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return;

		const queries = {
			xl: window.matchMedia("(min-width: 1536px)"),
			md: window.matchMedia("(min-width: 900px)"),
			sm: window.matchMedia("(min-width: 600px)"),
		};

		const updateColumnCount = () => {
			if (queries.xl.matches) {
				setColumnCount(5);
				return;
			}

			if (queries.md.matches) {
				setColumnCount(4);
				return;
			}

			if (queries.sm.matches) {
				setColumnCount(3);
				return;
			}

			setColumnCount(1);
		};

		updateColumnCount();

		const listeners = Object.values(queries).map((query) => {
			query.addEventListener("change", updateColumnCount);
			return () => {
				query.removeEventListener("change", updateColumnCount);
			};
		});

		return () => {
			for (const removeListener of listeners) {
				removeListener();
			}
		};
	}, []);

	return columnCount;
}

function FeatureCard({
	feature,
	index,
	hasEnteredView,
	isInTopRow,
}: {
	feature: FeatureItem;
	index: number;
	hasEnteredView: boolean;
	isInTopRow: boolean;
}) {
	const prefersReducedMotion = useReducedMotion();
	const shouldPlayEntryAnimation = isInTopRow && !prefersReducedMotion;
	const entryDelay = index * 0.075;

	return (
		<Box
			sx={{
				position: "relative",
			}}>
			<motion.div
				animate={
					shouldPlayEntryAnimation
						? hasEnteredView
							? { opacity: 1, y: 0 }
							: undefined
						: { opacity: 1, y: 0 }
				}
				initial={
					shouldPlayEntryAnimation
						? {
								opacity: 0.68,
								y: 14,
							}
						: false
				}
				transition={
					shouldPlayEntryAnimation
						? {
								opacity: {
									delay: entryDelay,
									duration: 0.22,
								},
								y: {
									...entrySpring,
									delay: entryDelay,
								},
							}
						: undefined
				}>
				<Card
					raised
					sx={{
						borderRadius: featureCardRadius,
						cursor: "pointer",
					}}>
					<CardActionArea>
						<CardContent>
							<Stack spacing={1}>
								<Box
									sx={{
										alignItems: "center",
										display: "flex",
										justifyContent: "space-between",
									}}>
									{feature.icon}
									{feature.comingSoon ? (
										<Chip
											color="success"
											icon={<Clock />}
											label="COMING SOON"
											size="small"
											sx={{ fontSize: 9, fontWeight: 600 }}
											variant="soft"
										/>
									) : null}
								</Box>
								<Typography variant="subtitle2">{feature.title}</Typography>
								<Typography
									color="text.secondary"
									variant="caption">
									{feature.description}
								</Typography>
							</Stack>
						</CardContent>
					</CardActionArea>
				</Card>
			</motion.div>
		</Box>
	);
}

export function FeaturesSection() {
	const [shouldDisableTopRowAnimation, setShouldDisableTopRowAnimation] =
		React.useState(false);

	React.useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return;

		const mediaQuery = window.matchMedia("(max-width: 599.95px)");
		const updateShouldDisableTopRowAnimation = () => {
			setShouldDisableTopRowAnimation(mediaQuery.matches);
		};

		updateShouldDisableTopRowAnimation();

		mediaQuery.addEventListener("change", updateShouldDisableTopRowAnimation);
		return () => {
			mediaQuery.removeEventListener(
				"change",
				updateShouldDisableTopRowAnimation,
			);
		};
	}, []);

	const features: FeatureItem[] = [
		{
			title: "Everything a GIS analyst needs, without the GIS software bill",
			description:
				"Queries, spatial analysis, and thematic styling — all in the browser, no installation required.",
			label: "Map Styling & Analysis",
			icon: (
				<MapPinArea
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Vector Data Support",
			description:
				"Supports Esri™ Shapefile, Mapinfo® Tab, KML, CSV, FileGeodatabase, and GeoJSON.",
			label: "Vector Data Support",
			icon: (
				<FilePlus
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Upload fast, fix less",
			description:
				"Drag files in, see per-file progress, and rely on smarter type detection, CRS handling, and clearer import errors.",
			label: "Data Import Workflow",
			icon: (
				<UploadSimple
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Ask Your Data Anything",
			description:
				"Build SQL-style queries on any attribute field — without writing SQL.",
			label: "Query Builder",
			icon: (
				<ListMagnifyingGlass
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Share with anyone — or just your team",
			description:
				"Public links for clients. Password-protected or login-required links for internal audiences. You decide who sees what.",
			label: "Collaboration & Sharing",
			icon: (
				<Link
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Share the exact map view you want people to see",
			description:
				"Link directly to a map centre, zoom level, and selected feature so viewers land in the right place.",
			label: "Collaboration & Sharing",
			icon: (
				<LinkSimple
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Edit together, with the right controls",
			description:
				"Collaborate with your team in real time with role-based permissions.",
			label: "Collaboration & Sharing",
			icon: (
				<Users
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "No Dataset Limits",
			description:
				"Upload and sync as many datasets as your storage allows — and add more storage any time you need it.",
			label: "Unlimited Data",
			icon: (
				<Database
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Style your data exactly how you want it",
			description:
				"Create thematic, heatmap, and custom graphic styles with instant updates, continuous numeric styling, rich colour ramps, and hundreds of preset icons.",
			label: "Custom Styles",
			icon: (
				<PaintBrush
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Build rich popups without code",
			description:
				"Create conditional, formatted popups with charts, images, tables, calculations, and dynamic field values.",
			label: "Popup Builder",
			icon: (
				<ChatCircleText
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "One dataset, any number of maps",
			description:
				"Update your data once and every map that uses it stays current. No duplicate uploads, no version drift.",
			label: "Unlimited Layers",
			icon: (
				<StackIcon
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Manage complex maps without the clutter",
			description:
				"Organize layers into folders, search them instantly, and control visibility and legends independently.",
			label: "Layer Management",
			icon: (
				<FolderSimple
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Fully Branded Maps",
			description:
				"Your domain, logo, colour palette, light or dark theme, and custom navigation — so your maps look like yours, not ours.",
			label: "Brand Identity",
			icon: (
				<Tag
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Maps in your language",
			description:
				"Override labels, UI text, and interface strings — so your maps feel local to your audience, wherever they are.",
			label: "Multi-Language Support",
			icon: (
				<Translate
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Drone imagery and remote sensing, ready to layer",
			description:
				"Upload GeoTIFF and COG rasters, work with multi-layer datasets, or connect WMS sources alongside your vector data.",
			label: "Raster & WMS Support",
			icon: (
				<Drone
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Find Any Location",
			description: "Global street-level address search.",
			label: "Address Lookup",
			icon: (
				<MapPin
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Search and filter your attribute data",
			description:
				"Filter by any field, combine conditions, and highlight results on the map instantly.",
			label: "Data Search & Filtering",
			icon: (
				<MagnifyingGlass
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Proximity, buffer, and intersection analysis",
			description:
				"Run spatial queries directly on your map — no desktop GIS software required.",
			label: "Spatial Analysis",
			icon: (
				<Globe
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Print-ready maps in minutes",
			description:
				"Build map exports with a live print composer and download PDF, PNG, JPEG, or SVG output.",
			label: "Export & Print",
			icon: (
				<Printer
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Stays fast, even with large datasets",
			description:
				"WebGL rendering and vector basemaps keep your maps smooth whether you've got 500 features or 5 million.",
			label: "Performance & Optimization",
			icon: (
				<Lightning
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Fast maps, every time",
			description:
				"Smart caching means your maps load quickly for every viewer, not just the first one.",
			label: "Performance & Optimization",
			icon: (
				<Timer
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Mobile-Friendly Maps",
			description:
				"Responsive layouts that work on phones and tablets — no separate mobile version needed.",
			label: "Performance & Optimization",
			icon: (
				<DeviceMobile
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Install MangoMap like an app",
			description:
				"Add MangoMap to the home screen on mobile or desktop for a more app-like experience.",
			label: "Mobile Experience",
			icon: (
				<DeviceMobile
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Role-Based Permissions",
			description:
				"Decide who can view, who can edit, and who can publish — per map or across your whole account.",
			label: "Security & Access Control",
			icon: (
				<ShieldCheck
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Access Restrictions",
			description:
				"Secure maps with password protection or user authentication.",
			label: "Security & Access Control",
			icon: (
				<Lock
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Connect external WMS layers",
			description:
				"Pull in layers from any OGC-compliant WMS — and combine them with your own data.",
			label: "External layers",
			icon: (
				<Cloud
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Custom Icons & Markers",
			description:
				"Upload your own icons and markers — so your map speaks the visual language your audience already knows.",
			label: "Customization & Theming",
			icon: (
				<Shapes
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Annotate Maps",
			description:
				"Add labels, drawings, and custom notes to highlight key areas.",
			label: "Customization & Theming",
			icon: (
				<Pencil
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
		{
			title: "Large-scale data, handled",
			description:
				"Upload and visualise high-volume vector and raster datasets. Coming soon.",
			label: "Customization & Theming",
			icon: (
				<MonitorArrowUp
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
			comingSoon: true,
		},
		{
			title: "And that's not all",
			description: "New tools ship regularly. Tell us what you need.",
			label: "And More!",
			icon: (
				<Rocket
					color="var(--mui-palette-primary-main)"
					size={24}
					weight="duotone"
				/>
			),
		},
	];

	const [showAll, setShowAll] = React.useState(false);
	const collapseTimeoutRef = React.useRef<number | null>(null);
	const sectionRootRef = React.useRef<HTMLDivElement | null>(null);
	const sectionRef = React.useRef<HTMLDivElement | null>(null);
	const prefersReducedMotion = useReducedMotion();
	const activeColumnCount = useActiveFeatureColumnCount();
	const hasEnteredView = useInView(sectionRef, {
		amount: 0.35,
		once: true,
	});

	React.useEffect(() => {
		return () => {
			if (collapseTimeoutRef.current !== null) {
				window.clearTimeout(collapseTimeoutRef.current);
			}
		};
	}, []);

	const handleToggleShowAll = () => {
		if (collapseTimeoutRef.current !== null) {
			window.clearTimeout(collapseTimeoutRef.current);
			collapseTimeoutRef.current = null;
		}

		if (!showAll) {
			setShowAll(true);
			return;
		}

		if (prefersReducedMotion) {
			sectionRootRef.current?.scrollIntoView({
				behavior: "auto",
				block: "start",
			});
			setShowAll(false);
			return;
		}

		const scrollDistance = Math.abs(
			sectionRootRef.current?.getBoundingClientRect().top ?? 0,
		);
		const collapseDelay = Math.min(Math.max(scrollDistance * 0.25, 400), 900);

		sectionRootRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});

		collapseTimeoutRef.current = window.setTimeout(() => {
			setShowAll(false);
			collapseTimeoutRef.current = null;
		}, collapseDelay);
	};

	return (
		<Box
			ref={sectionRootRef}
			sx={{
				py: { xs: "60px", sm: "120px" },
				scrollMarginTop: "96px",
			}}>
			<Container maxWidth="xl">
				<Typography
					sx={{ mb: 4, textAlign: "center", fontFamily: headingFontFamily }}
					variant="h3">
					Key Features
				</Typography>
				<Box
					ref={sectionRef}
					sx={{
						position: "relative",
					}}>
					<Box
						sx={{
							maxHeight: showAll ? "none" : 300,
							overflow: "hidden",
							position: "relative",
							pb: featureGridSafeArea,
							pt: featureGridSafeArea,
							px: featureGridSafeArea,
						}}>
						<Masonry
							columns={{ xs: 1, sm: 3, md: 4, xl: 5 }}
							defaultColumns={5}
							defaultHeight={200}
							defaultSpacing={2}
							sequential
							spacing={{ xs: 2, sm: 3, md: 4, xl: 5 }}>
							{features.map((feature, index) => (
								<FeatureCard
									feature={feature}
									hasEnteredView={prefersReducedMotion ? false : hasEnteredView}
									index={index}
									isInTopRow={
										!shouldDisableTopRowAnimation && index < activeColumnCount
									}
									key={feature.title}
								/>
							))}
						</Masonry>
						{!showAll && (
							<Box
								sx={{
									background: featureGridFadeTint,
									bottom: 0,
									height: 80,
									left: 0,
									pointerEvents: "none",
									position: "absolute",
									width: "100%",
								}}
							/>
						)}
					</Box>
				</Box>
			</Container>
			<Stack sx={{ alignItems: "center", mt: 2 }}>
				<Button
					color="secondary"
					onClick={handleToggleShowAll}
					size="small"
					variant="outlined">
					{showAll ? "Show less" : "Show more"}
				</Button>
			</Stack>
		</Box>
	);
}
